import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import _ from 'lodash';
import { useUpdateSubmissionServices, validationUtil } from 'premier-capability-hooks';
import React, { useCallback, useContext, useEffect, useState, useMemo } from 'react';
import { CoverageComponent } from "premier-capability-policyjob-react";
import { CodegenContext } from '../../components/CodegenContext';
import messages from'./CoveragesPage.messages';
import presentationMetadata from './CoveragesPage.metadata.json5';
import styles from '../MarPle/MarPlePage.module.scss';
import AddlInterestTextBoxComponent from "../../components/AddlInterestTextBoxComponent/AddlInterestTextBoxComponent";
import { TranslatorContext } from '@jutro/legacy/locale';
import { BreakpointTrackerContext } from '@jutro/legacy/layout';

function CoveragesPage(props) {
    const [loadingClause] = useState();
    const { wizardData: submissionVM, updateWizardData, currentStep } = props;
    const { pageKey } = currentStep;
    const { lineName } = useContext(CodegenContext);
    // eslint-disable-next-line security/detect-object-injection
    const metadata = presentationMetadata[pageKey];
    const breakpoint = useContext(BreakpointTrackerContext);
    const translator = useContext(TranslatorContext);
    const { updateSubmission } = useUpdateSubmissionServices();
    const {
        onValidate,
        initialValidation,
        registerComponentValidation,
        registerInitialComponentValidation,
    } = useValidation('CoveragesPage');
    const clausesBasePath = `lobData.${lineName}.lineCoverages`;

    const resolvers = {
        resolveCallbackMap: {},
        resolveComponentMap: {
            CoverageComponent,
            AddlInterestTextBoxComponent
        },
        resolveClassNameMap: styles
    };

    const coveragesOverrides = useMemo(() => {
        const boatList = _.get(submissionVM, 'lobData.recMarLine.lineCoverages.boaRisCoverages.coverages', []);
        let overrides = [];

        if (!_.isEmpty(boatList)) {
            overrides = boatList?.children.map((boat, index) => ({
                [`CoverageComponent${index}`]: {
                    wizardData: submissionVM,
                    updateWizardData,
                    clausesBasePath: `lobData.recMarLine.lineCoverages.boaRisCoverages.coverages.children[${index}].coverages`,
                    title: messages.covAccordion,
                    clasueType: 'coverages',
                    pathToGet: `recMarLine.boaRisCoverages.coverages[${index}].coverages`
                },
                [`additionalInterest${index}`]: {
                    wizardData: submissionVM,
                    updateWizardData,
                    addlIntPath: `lobData.recMarLine.coverables.boats.children[${index}].additionalInterest`
                },
                [`coveragesAccordionDetails${index}`]: {
                    title: translator(messages.coverages, { count: `${index + 1}` })
                }
            }));
        }

        return Object.assign({}, ...overrides);
    }, [submissionVM, updateWizardData, translator]);

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        ...coveragesOverrides
    };

    const shouldPageSkip = useCallback(() => {
        const coverages = _.get(submissionVM.value, clausesBasePath);
        const jobType = _.get(submissionVM.value, 'baseData.jobType');

        if (jobType === 'Submission') {
            return true;
        }

        const status = _.get(submissionVM.value, 'status');

        return (
            status === 'Quoted' ||
            (coverages && !_.find(coverages, { isValid: false }))
        );
    }, [clausesBasePath, submissionVM.value]);

    const isPageLoading = useCallback(
        () => _.isUndefined(loadingClause),
        [loadingClause]
    );

    useEffect(() => {
        registerComponentValidation(isPageLoading);
        registerInitialComponentValidation(shouldPageSkip);
    }, [
        isPageLoading,
        registerComponentValidation,
        registerInitialComponentValidation,
        shouldPageSkip,
    ]);

    const onNext = useCallback(
        async () => {

            const { productCode } = submissionVM.value.baseData;
            const lobLineName = lineName[productCode];

            submissionVM.value = await updateSubmission(lobLineName, 'AdditionalInterest', submissionVM);

            return submissionVM;
        },
        [lineName, submissionVM, updateSubmission]
    );

    const onSave = useCallback(
        async () => {
            const subVMCopy = _.clone(submissionVM)
            const productCode = _.get(subVMCopy.value, 'baseData.productCode');

            validationUtil.unSetIsValidationReqFlag(subVMCopy.lobData.recMarLine.coverables.boats, ' ')

            const lobLineName = productCode ? lineName[productCode] : '';

            await updateSubmission(lobLineName, 'AdditionalInterest', subVMCopy);
                            
            return submissionVM;
        },
        [lineName, submissionVM, updateSubmission]
    );

    return (
        <WizardPage
            onNext={onNext}
            onSave={onSave}
            skipWhen={() => initialValidation}
            disableNext={!_.get(submissionVM, 'lobData.recMarLine.coverables.boats.aspects.subtreeValid')}
        >
            <ViewModelForm
                uiProps={metadata.content}
                model={submissionVM}
                overrideProps={overrideProps}
                onValidationChange={onValidate}
                onModelChange={updateWizardData}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
            />
        </WizardPage>
    );
}

CoveragesPage.propTypes = {
    ...wizardProps,
};

export default CoveragesPage;
